/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventLocationFields.tsx
import css from '@emotion/css/macro'
import { useState } from 'react'
import { useFormikContext } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import { useVenues } from '@sportninja/common/features/game/gameForm/hooks/useGameOptions'
import {
  buildFacilityOptions,
  buildVenueOptions,
  CUSTOM_LOCATION_LABEL,
  CUSTOM_LOCATION_VALUE,
  resolveVenueTimezone,
} from '@sportninja/common/features/event/venueSelection'
import type { EventFormValues } from '@sportninja/common/features/event/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { paragraphSmall } from 'src/components/css'
import GameSearchField from 'src/features/game/gameForm/components/GameSearchField'
import { FieldRow } from 'src/features/profile/player/setupModal/components/FormikField'

interface EventLocationFieldsProps {
  /** The competition the event belongs to — scopes the venue list. */
  scheduleId: string
}

/**
 * Location and Facility, mirroring the add/edit Game modal.
 *
 * Picking a location puts the form in VENUE mode: the address fields go away
 * (the venue owns them) and the payload carries `venue_id`/`facility_id`. The
 * Timezone select goes away too, but ONLY when the venue actually has a zone —
 * a venue without one leaves the zone the admin's to pick.
 *
 * "Use A Custom Location" is a footer row under the list rather than an option
 * in it, styled like the Game modal's "Create a new location", so it is not
 * mistaken for a venue by that name.
 *
 * Only rendered for a competition event: `/venues` is scoped to a schedule, so
 * a team-owned event has no list to offer.
 */
const EventLocationFields: React.FC<EventLocationFieldsProps> = ({
  scheduleId,
}) => {
  const { values, setValues } = useFormikContext<EventFormValues>()
  // Reuses the Game modal's query and cache — same endpoint, same key, so
  // opening this modal after the Game one costs no request.
  const { venues, isLoading } = useVenues(scheduleId)
  // Controlled so the custom-location footer can close the list. Unlike the
  // Game modal's equivalent row, this one opens no modal to steal focus, so
  // without this the dropdown stays open after the click.
  const [isOpen, setIsOpen] = useState(false)

  const selectedVenue = venues.find((venue) => venue.id === values.venueId)
  // The custom choice is shown as the field's VALUE (a disabled option that is
  // never in the dropdown), so the field reads as answered rather than empty
  // while the address block sits below it.
  const venueOptions = values.isCustomLocation
    ? [
        ...buildVenueOptions(venues),
        {
          value: CUSTOM_LOCATION_VALUE,
          label: capitalizeWords(CUSTOM_LOCATION_LABEL),
        },
      ]
    : buildVenueOptions(venues)
  const facilityOptions = buildFacilityOptions(selectedVenue)

  const handleVenueChange = (next: string | null) => {
    // One update rather than three: each write re-renders the whole form.
    setValues((current) => {
      // Cleared via antd's ✕: back to the untouched state — no venue, and no
      // custom-location choice either, so the address fields stay hidden. The
      // zone reverts to what the form was SEEDED with, since keeping the
      // ex-venue's would strand the event in the timezone of a location that
      // is no longer selected.
      if (next === null) {
        return {
          ...current,
          venueId: '',
          facilityId: '',
          isCustomLocation: false,
          timezone: current.defaultTimezone,
        }
      }

      // The sentinel is only ever the field's displayed VALUE, never a real
      // option — but guard anyway so it can never be posted as a venue_id.
      if (next === CUSTOM_LOCATION_VALUE) {
        return current
      }

      const venue = venues.find((candidate) => candidate.id === next)
      const venueTimezone = resolveVenueTimezone(venue)

      return {
        ...current,
        venueId: next,
        // The facility list belongs to the OLD venue — keeping the id would
        // post a facility that does not exist under the new location.
        facilityId: '',
        isCustomLocation: false,
        // The venue's zone becomes the event's. A venue WITHOUT one leaves the
        // current zone alone and keeps the Timezone select visible, rather
        // than blanking a zone the form already had.
        timezone: venueTimezone ?? current.timezone,
      }
    })
  }

  const handleFacilityChange = (next: string | null) => {
    setValues((current) => ({ ...current, facilityId: next ?? '' }))
  }

  /**
   * Chooses the custom address instead of a venue — the explicit answer that
   * reveals the address fields. Deliberately NOT the same as clearing the
   * field, which returns to the untouched state.
   */
  const handleCustomLocation = () => {
    setValues((current) => ({
      ...current,
      venueId: '',
      facilityId: '',
      isCustomLocation: true,
      // No venue means no venue zone — back to the form's seeded zone, with
      // the Timezone select visible again.
      timezone: current.defaultTimezone,
    }))
    setIsOpen(false)
  }

  const customLocationRow = (
    <button
      type='button'
      css={customRowStyle}
      onMouseDown={(event) => {
        // The select closes on blur; mousedown fires first so the click is not
        // lost to the dropdown unmounting under the pointer.
        event.preventDefault()
        handleCustomLocation()
      }}
    >
      {capitalizeWords(CUSTOM_LOCATION_LABEL)}
    </button>
  )

  return (
    <FieldRow cols={2} collapseAt={420}>
      <GameSearchField
        label={capitalizeWords('Location')}
        value={
          values.venueId ||
          (values.isCustomLocation ? CUSTOM_LOCATION_VALUE : null)
        }
        options={venueOptions}
        onChange={handleVenueChange}
        placeholder={capitalizeWords('Select A Location')}
        loading={isLoading}
        footer={customLocationRow}
        open={isOpen}
        onOpenChange={setIsOpen}
      />
      <div css={facilityCellStyle}>
        <GameSearchField
          label={capitalizeWords('Facility')}
          value={values.facilityId || null}
          options={facilityOptions}
          onChange={handleFacilityChange}
          placeholder={capitalizeWords('Select A Facility')}
          // Matches the Game modal: a facility is meaningless without a
          // location, and a location may legitimately have none.
          disabled={!values.venueId || facilityOptions.length === 0}
        />
      </div>
    </FieldRow>
  )
}

export default EventLocationFields

// ---- styled ----
/* Matches the Game modal's "Create a new location" row (`createRowStyle` in
   GameModalContainer) — PRIMARY-coloured so it reads as an action rather than
   as one more venue in the list. */
const customRowStyle = css`
  ${paragraphSmall}
  display: block;
  width: 100%;
  padding: 6px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${colors.PRIMARY};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${colors.SECONDARY_200};
  }
`

/* GameSearchField carries no bottom margin of its own, so the pair sits on the
   form's 16px gap like every other row. The wrapper keeps the disabled facility
   field from collapsing narrower than its sibling. */
const facilityCellStyle = css`
  min-width: 0;
`
